<template>
  <button
    :type="type"
    :class="[
      'flex items-center justify-center font-medium rounded-lg transition',
      sizeClasses[size!],
      variantClasses[variant!],
      className,
      { 'cursor-not-allowed opacity-50': disabled },
      { 'border-2 border-green-500': template === 'filter' && active === 'active' },
      { 'border-2 border-red-500': template === 'filter' && active === 'inactive' },
    ]"
    @click="onClick"
    :disabled="disabled"
  >
    <span v-if="template && icons[template]" class="flex items-center">
      <component :size="18" :is="icons[template]" />
    </span>

    <span v-if="startIcon" class="flex items-center"> <component :is="startIcon" /> </span>

    <template v-if="template && actionTemplates[template]">
      {{ $t(`actions.${template}`) }}
    </template>

    <slot></slot>

    <span v-if="endIcon" class="flex items-center"> <component :is="endIcon" /> </span>
  </button>
</template>

<script setup lang="ts">
import { EditIcon } from '@/shared/icons'

import { Trash, Save, X, Plus, Settings2, Search } from '@lucide/vue'

const actionTemplates = {
  add: true,
  apply: true,
  save: true,
  remove: true,
  update: true,
  filter: true,
  close: true,
}

const icons = {
  add: Plus,
  apply: Search,
  save: Save,
  remove: Trash,
  update: EditIcon,
  filter: Settings2,
  close: X,
}

const sizeClasses = {
  xs: 'h-8 px-3 text-xs gap-1.5 sm:h-9 sm:px-4 sm:text-sm sm:gap-2 rounded-lg',
  sm: 'px-3 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm',
  md: 'px-3.5 py-2.5 text-xs sm:px-5 sm:py-4 sm:text-sm',
}

const variantClasses = {
  primary:
    'bg-primary text-white hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700 dark:hover:bg-white/[0.03] dark:hover:text-gray-300',
  brand: 'bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600 disabled:bg-brand-300',
  compact:
    'border border-border text-foreground hover:bg-primary hover:text-white dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700 dark:hover:bg-white/[0.03] dark:hover:text-gray-300',
}

interface ButtonProps {
  size?: keyof typeof sizeClasses
  variant?: keyof typeof variantClasses
  startIcon?: object
  endIcon?: object
  onClick?: () => void
  className?: string
  disabled?: boolean
  type?: 'submit' | 'button' | 'reset'
  template?: keyof typeof actionTemplates
  active?: string
}

const props = withDefaults(defineProps<ButtonProps>(), {
  size: 'xs',
  variant: 'primary',
  className: '',
  disabled: false,
  type: 'button',
  active: '',
})

const onClick = () => {
  if (!props.disabled && props.onClick) {
    props.onClick()
  }
}
</script>
